import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  SignalIcon,
  CreditCardIcon,
  ChartBarIcon,
  BellIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ClockIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserGroupIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../utils/axios';
import UserSessionActivity from '../components/users/UserSessionActivity';
import DeviceIcon from '../components/DeviceIcon';
import { modelLabel } from '../utils/modelLabel';
import NotificationPreferencesEditor from '../components/users/NotificationPreferencesEditor';
import PaymentInformation from '../components/users/PaymentInformation';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../config/permissions';

// Status Change Modal Component
const StatusChangeModal = ({ isOpen, onClose, user, onConfirm, isLoading }) => {
  if (!isOpen) return null;

  const isDeactivating = user?.active;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-full ${isDeactivating ? 'bg-red-100' : 'bg-green-100'}`}>
                  <ExclamationTriangleIcon className={`w-6 h-6 ${isDeactivating ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isDeactivating ? 'Deactivate User Account' : 'Activate User Account'}
                </h3>
              </div>
              
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  You are about to <span className="font-semibold">{isDeactivating ? 'deactivate' : 'activate'}</span> the account for:
                </p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                  <p className="text-gray-600">{user?.email}</p>
                </div>
                
                {isDeactivating ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 font-medium mb-1">This will:</p>
                    <ul className="text-red-700 space-y-1 list-disc list-inside">
                      <li>Prevent the user from logging in</li>
                      <li>Disconnect any active sessions</li>
                      <li>Stop all notifications to this user</li>
                      <li>Disable access to all devices</li>
                    </ul>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-800 font-medium mb-1">This will:</p>
                    <ul className="text-green-700 space-y-1 list-disc list-inside">
                      <li>Allow the user to log in again</li>
                      <li>Restore access to assigned devices</li>
                      <li>Re-enable notifications based on preferences</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                  isDeactivating 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isLoading ? 'Processing...' : (isDeactivating ? 'Deactivate Account' : 'Activate Account')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Live Connection Component
const LiveConnectionCard = ({ socketDebugInfo, activeSubscriptions }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!socketDebugInfo || socketDebugInfo.length === 0) return null;
  
  return (
    <div className="border border-green-200 rounded-lg overflow-hidden">
      {/* Dropdown Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <SignalIcon className="w-4 h-4 text-green-600" />
          <span className="text-sm font-semibold text-green-800">
            Live User Connection
          </span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
            {socketDebugInfo.length} Active {socketDebugInfo.length > 1 ? 'Sessions' : 'Session'}
          </span>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-green-600 transition-transform ${
          isExpanded ? 'rotate-180' : ''
        }`} />
      </button>
      
      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-green-200 bg-gradient-to-b from-green-50/50 to-white p-4">
          {socketDebugInfo.map((socket, idx) => (
            <div key={`socket-${idx}`} className={`${idx > 0 ? 'border-t border-green-100 pt-3' : ''}`}>
              {/* Socket Header */}
              {socketDebugInfo.length > 1 && (
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-700">
                    Socket Connection #{idx + 1}
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    socket.readyState === 1 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {socket.readyState === 1 ? 'OPEN' : 'CONNECTING'}
                  </span>
                </div>
              )}
              
              {/* Connection Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                <div className="bg-white/70 rounded p-2">
                  <p className="text-gray-600 mb-0.5">Remote Address</p>
                  <p className="font-medium text-gray-900 font-mono text-[10px]">
                    {socket.remoteAddress}:{socket.remotePort}
                  </p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-gray-600 mb-0.5">Connected At</p>
                  <p className="font-medium text-gray-900">
                    {socket.connectionTime ? 
                      new Date(socket.connectionTime).toLocaleTimeString() : 'N/A'}
                  </p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-gray-600 mb-0.5">Is Alive</p>
                  <p className={`font-medium ${
                    socket.isAlive ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {socket.isAlive ? 'Yes' : 'No'}
                  </p>
                </div>
                <div className="bg-white/70 rounded p-2">
                  <p className="text-gray-600 mb-0.5">Buffer</p>
                  <p className="font-medium text-gray-900">
                    {socket.bufferedAmount || 0} bytes
                  </p>
                </div>
              </div>
              
              {/* User Info from Token */}
              {socket.userInfo && (
                <div className="bg-white/70 rounded p-2 mb-3">
                  <p className="text-xs text-gray-600 mb-1.5 font-semibold">User Info from Token</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">User ID:</span>
                      <p className="font-mono text-[10px] text-gray-900">{socket.userInfo.userId}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <p className="font-medium text-gray-900">{socket.userInfo.name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium text-gray-900">{socket.userInfo.email || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Role:</span>
                      <p className="font-medium text-gray-900">{socket.userInfo.role || 'N/A'}</p>
                    </div>
                  </div>
                  {socket.userInfo.devices && socket.userInfo.devices.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Authorized Devices: </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {socket.userInfo.devices.map((device, didx) => (
                          <span key={`socket-${idx}-device-${didx}`} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {device.serial} ({device.model || 'Unknown'})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Subscriber Info */}
              {socket.subscriberInfo && (
                <div className="bg-white/70 rounded p-2 mb-3">
                  <p className="text-xs text-gray-600 mb-1.5 font-semibold">Subscriber Info</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Serial:</span>
                      <p className="font-mono text-gray-900">{socket.subscriberInfo.serial}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Topics:</span>
                      <p className="font-medium text-gray-900">{socket.subscriberInfo.topics?.join(', ') || 'None'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Subscribed At:</span>
                      <p className="font-medium text-gray-900">
                        {socket.subscriberInfo.subscribedAt ? 
                          new Date(socket.subscriberInfo.subscribedAt).toLocaleTimeString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Active Subscriptions */}
              {activeSubscriptions && activeSubscriptions.length > 0 && idx === 0 && (
                <div className="bg-white/70 rounded p-2">
                  <p className="text-xs text-gray-600 mb-1.5 font-semibold">
                    Active Subscriptions ({activeSubscriptions.length})
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {activeSubscriptions.map((sub, subIdx) => (
                      <div key={`active-sub-${subIdx}-${sub.deviceSerial}`} className="flex items-center justify-between bg-green-50 rounded px-2 py-1">
                        <div className="text-xs">
                          <span className="font-medium text-gray-900">{sub.topic}</span>
                          <span className="text-gray-500 ml-1">on {sub.deviceSerial}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                          <span className="text-xs text-green-600 font-medium">Live</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 overflow-hidden"
            style={{ pointerEvents: 'auto' }}
          >
            <div className="p-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0">
      <Icon className="w-5 h-5 text-gray-400" />
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value || 'N/A'}</p>
    </div>
  </div>
);

// Editable Info Item Component
const EditableInfoItem = ({ icon: Icon, label, value, field, onChange, type = "text", disabled = false, placeholder, required = false }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
      <Icon className="w-4 h-4 text-gray-400" />
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {disabled ? (
      <p className="font-medium text-gray-900 bg-gray-50 px-3 py-2 rounded-md">{value || 'N/A'}</p>
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                   placeholder-gray-400 transition-colors duration-200
                   hover:border-gray-400"
      />
    )}
  </div>
);

const UserDetail = ({ users = [], subscribers = [], devices = [] }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentStaff } = useAuth();
  const isDeveloper = hasRole(currentStaff?.role, 'developer');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [socketDebugInfo, setSocketDebugInfo] = useState(null);
  // Removed stats state as it was causing phantom devices to appear
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteDeviceLoading, setDeleteDeviceLoading] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [customDeleteReason, setCustomDeleteReason] = useState('');
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceNickname, setDeviceNickname] = useState('');
  const [addDeviceLoading, setAddDeviceLoading] = useState(false);

  // Update online status and subscriptions from props when available
  useEffect(() => {
    const subscriber = subscribers.find(sub => sub.userId === id);
    setIsOnline(!!subscriber);
    
    const subscriptions = subscriber?.subscriptions?.map(sub => ({
      deviceSerial: sub.serial,
      topic: sub.topic,
      subscribedAt: new Date().toISOString()
    })) || [];
    
    setActiveSubscriptions(subscriptions);
  }, [subscribers, id]);

  // Effect to handle data fetching
  useEffect(() => {
    // Reset state when ID changes
    setLoading(true);
    setError(null);
    setUser(null);

    // First check if we have complete user data in props
    const propsUser = users.find(u => u._id === id);

    // Check if props user has complete device data
    const hasCompleteData = propsUser &&
      propsUser.deviceStatuses && // Has device statuses
      ((propsUser.devices && propsUser.devices.length > 0 &&
        propsUser.devices[0].deviceId &&
        typeof propsUser.devices[0].deviceId === 'object') || // Devices are populated
       (propsUser.dryermasterId && typeof propsUser.dryermasterId === 'object')); // Or legacy field is populated

    if (propsUser && hasCompleteData) {
      // Props has complete data, use it directly
      setUser(propsUser);
      setLoading(false);

      // Update online status and subscriptions if available
      if (propsUser.isOnline !== undefined) {
        setIsOnline(propsUser.isOnline);
      }
      if (propsUser.activeSubscriptions) {
        setActiveSubscriptions(propsUser.activeSubscriptions);
      }
    } else {
      // Props data is incomplete or missing, fetch from API
      fetchUser();
    }
  }, [id, users]); // Include users in deps to update when props change

  const fetchUser = async () => {
    setError(null);
    
    try {
      const response = await axiosInstance.get(`/v1/users/${id}`);
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        setUser(userData);
        
        // Update online status and subscriptions from the API response
        if (userData.isOnline !== undefined) {
          setIsOnline(userData.isOnline);
        }
        if (userData.activeSubscriptions) {
          setActiveSubscriptions(userData.activeSubscriptions);
        }
      } else {
        throw new Error('Failed to fetch user details');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      // Only set error after a minimum loading time to avoid flash
      setTimeout(() => {
        setError(err.message || 'Failed to fetch user details');
      }, 500);
    } finally {
      // Ensure minimum loading time for better UX
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  };

  // Handle edit mode
  const handleEdit = () => {
    setEditedUser({ ...user });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedUser(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axiosInstance.put(`/v1/users/${id}`, editedUser);
      if (response.data.success) {
        setUser(response.data.data);
        setIsEditing(false);
        setEditedUser(null);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  // Handle setting default device
  const handleSetDefaultDevice = async (deviceSerial) => {
    try {
      const response = await axiosInstance.patch(`/v1/users/${id}/default-device`, {
        deviceSerial
      });

      if (response.data.success) {
        // Update local user state with the new data
        setUser(response.data.data);

        // Show success message
        const message = `Default device changed to ${deviceSerial}`;
        // You can add a toast notification here if you have one
        console.log(message);
      }
    } catch (error) {
      console.error('Error updating default device:', error);
      // You can add error toast here
    }
  };

  // Handle account status change
  const handleStatusChange = async () => {
    setStatusChangeLoading(true);
    try {
      const newStatus = !user.active;
      const response = await axiosInstance.patch(`/v1/users/${id}/status`, { active: newStatus });

      if (response.data.success) {
        setUser(response.data.data);
        setShowStatusModal(false);
        // Show success message (you can add a toast notification here)
      }
    } catch (err) {
      console.error('Error changing user status:', err);
      alert('Failed to change user status');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Fetch available devices when modal opens
  useEffect(() => {
    if (showAddDeviceModal) {
      fetchAvailableDevices();
    }
  }, [showAddDeviceModal]);

  // Filter devices based on search
  useEffect(() => {
    if (searchTerm) {
      const filtered = availableDevices.filter(device =>
        device.serial.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.location?.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDevices(filtered);
    } else {
      setFilteredDevices(availableDevices);
    }
  }, [searchTerm, availableDevices]);

  const fetchAvailableDevices = async () => {
    setAddDeviceLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/device-details/complete');

      if (response.data.success) {
        const devices = response.data.devices;

        // Process devices to add availability status
        const processedDevices = devices.map(device => {
          // Check if current user already has this device (and it's not removed)
          const currentUserDevice = user.devices?.find(
            d => d.deviceId.serial === device.serial
          );
          const currentUserHasDevice = currentUserDevice && !currentUserDevice.removedAt;

          return {
            ...device,
            currentUserHasDevice,
            isAvailable: !device.passwordUsed && device.owners.length === 0,
            assignedUsers: device.owners || []
          };
        });

        setAvailableDevices(processedDevices);
        setFilteredDevices(processedDevices);
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to fetch available devices');
    } finally {
      setAddDeviceLoading(false);
    }
  };

  const handleAddDevice = async () => {
    if (!selectedDevice) return;

    setAddDeviceLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(`/v1/users/${id}/devices`, {
        deviceSerial: selectedDevice.serial,
        nickname: deviceNickname || null
      });

      if (response.data.success) {
        // Update user data
        setUser(response.data.data);

        // Close modal and reset
        setShowAddDeviceModal(false);
        setSelectedDevice(null);
        setDeviceNickname('');
        setSearchTerm('');
      }
    } catch (err) {
      console.error('Error adding device:', err);
      alert(err.response?.data?.message || 'Failed to add device');
    } finally {
      setAddDeviceLoading(false);
    }
  };

  // Handle device deletion
  const handleDeleteDevice = async () => {
    if (!deviceToDelete) return;

    // Determine the final reason
    let finalReason = deleteReason;
    if (deleteReason === 'custom' && customDeleteReason) {
      finalReason = customDeleteReason;
    } else if (!deleteReason) {
      finalReason = 'Removed by administrator';
    }

    setDeleteDeviceLoading(true);
    try {
      const response = await axiosInstance.delete(`/v1/users/${id}/devices`, {
        data: {
          deviceSerial: deviceToDelete.serial,
          reason: finalReason
        }
      });

      if (response.data.success) {
        setUser(response.data.data);
        setShowDeleteDeviceModal(false);
        setDeviceToDelete(null);
        setDeleteReason('');
        setCustomDeleteReason('');
      }
    } catch (err) {
      console.error('Error removing device:', err);
      alert(err.response?.data?.message || 'Failed to remove device access');
    } finally {
      setDeleteDeviceLoading(false);
    }
  };

  // SSE connection for real-time updates
  useEffect(() => {
    if (!user || !id) return;

    const token = localStorage.getItem('token');
    const sseUrl = `${import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse'}/events?token=${token}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle system status updates
        if (data.type === 'system_status' && data.stats) {
          // Check if this user is in the subscribers list
          const userSubscriber = data.stats.subscribers?.find(sub => sub.userId === id);
          if (userSubscriber) {
            setIsOnline(true);
            // Update subscriptions
            const subscriptions = userSubscriber.subscriptions?.map(sub => ({
              deviceSerial: sub.serial,
              topic: sub.topic,
              subscribedAt: new Date().toISOString()
            })) || [];
            setActiveSubscriptions(subscriptions);
            
            // Store socket debug information
            setSocketDebugInfo(userSubscriber.sockets || null);
          } else {
            // User not in subscribers list means they're offline
            setIsOnline(false);
            setActiveSubscriptions([]);
            setSocketDebugInfo(null);
          }
        }
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
    };

    return () => {
      eventSource.close();
    };
  }, [user?.email, id]); // Watch for email change to ensure proper cleanup

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        {/* Loading Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded"></div>
                <div>
                  <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 w-32 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="h-8 w-24 bg-gray-200 rounded-full"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="h-4 w-16 bg-gray-200 rounded mx-auto mb-2"></div>
                <div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-4 w-16 bg-gray-200 rounded mx-auto mb-2"></div>
                <div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-4 w-16 bg-gray-200 rounded mx-auto mb-2"></div>
                <div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-4 w-16 bg-gray-200 rounded mx-auto mb-2"></div>
                <div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Sections */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={`loading-section-${i}`} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                    <div className="h-5 w-32 bg-gray-200 rounded"></div>
                  </div>
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Loading message */}
        <div className="text-center text-gray-500 text-sm">
          Loading user details...
        </div>
      </motion.div>
    );
  }

  if (error || !user) {
    return (
      <div className="bg-red-50 rounded-lg p-6 text-center">
        <p className="text-red-600">{error || 'User not found'}</p>
        <button
          onClick={() => navigate('/dashboard/users')}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const StatusIcon = isOnline ? CheckCircleIcon : XCircleIcon;
  const statusColor = isOnline ? 'text-green-500' : 'text-gray-400';
  const statusBgColor = isOnline ? 'bg-green-50' : 'bg-gray-50';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard/users')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 capitalize">
                {user.firstName} {user.lastName}
              </h1>
              <p className="text-gray-600 mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusBgColor}`}>
              <StatusIcon className={`h-5 w-5 ${statusColor}`} />
              <span className={`text-sm font-medium ${statusColor}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600">Role</p>
            <p className="text-lg font-semibold text-gray-900 capitalize">{user.role}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Status</p>
            {/* Developers click the status to activate or deactivate the account */}
            {isDeveloper ? (
              <button
                onClick={() => setShowStatusModal(true)}
                title={user.active ? 'Click to deactivate this account' : 'Click to activate this account'}
                className={`text-lg font-semibold hover:underline ${user.active ? 'text-green-600' : 'text-red-600'}`}
              >
                {user.active ? 'Active' : 'Inactive'}
              </button>
            ) : (
              <p className={`text-lg font-semibold ${user.active ? 'text-green-600' : 'text-red-600'}`}>
                {user.active ? 'Active' : 'Inactive'}
              </p>
            )}
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Member Since</p>
            <p className="text-lg font-semibold text-gray-900">
              {new Date(user.createdAt).getFullYear()}
            </p>
          </div>
          {isOnline && (
            <div className="text-center">
              <p className="text-sm text-gray-600">Active Subscriptions</p>
              <p className="text-lg font-semibold text-blue-600">
                {activeSubscriptions.length}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Collapsible Sections */}
      <div className="space-y-6">
        {/* Device Information - At the top */}
        <CollapsibleSection
          title="Device Information"
          icon={DeviceIcon}
          defaultOpen={true}
        >
          {(() => {
            // Combine all device sources
            const allDevices = new Map();

            // Add devices from user's devices array (new approach)
            if (user.devices && user.devices.length > 0) {
              user.devices.forEach(device => {
                // Only add devices with valid deviceId and serial number
                if (device.deviceId && device.deviceId.serial) {
                  const deviceData = device.deviceId;

                  allDevices.set(deviceData.serial, {
                    serial: deviceData.serial,
                    model: deviceData.model || null,
                    nickname: device.nickname || null, // Include nickname from devices array
                    type: 'registered',
                    isDefault: device.isDefault,
                    createdAt: deviceData.createdAt,
                    lastSeen: user.deviceStatuses?.[deviceData.serial]?.lastSeen || deviceData.lastSeen,
                    isOnline: user.deviceStatuses?.[deviceData.serial]?.isOnline || false,
                    connectedAt: user.deviceStatuses?.[deviceData.serial]?.connectedAt,
                    removedAt: device.removedAt || null,
                    removedBy: device.removedBy || null,
                    removalReason: device.removalReason || null,
                    isRemoved: !!device.removedAt
                  });
                }
              });
            }

            // ALSO add legacy dryermasterId if it exists and not already in devices array
            // This ensures we don't miss any devices during the migration period
            if (user.dryermasterId && user.dryermasterId.serial && !allDevices.has(user.dryermasterId.serial)) {
              allDevices.set(user.dryermasterId.serial, {
                serial: user.dryermasterId.serial,
                model: user.dryermasterId.model,
                nickname: null, // Legacy field doesn't have nickname
                type: 'registered',
                isDefault: false, // Legacy device, not necessarily default anymore
                lastSeen: user.deviceStatuses?.[user.dryermasterId.serial]?.lastSeen || null,
                isOnline: user.deviceStatuses?.[user.dryermasterId.serial]?.isOnline || false,
                connectedAt: user.deviceStatuses?.[user.dryermasterId.serial]?.connectedAt
              });
            }
            
            // REMOVED: Don't add accessed devices from stats as they cause phantom devices
            // Only show devices that the user actually owns (from user.devices array)

            const deviceList = Array.from(allDevices.values());

            // Separate active and removed devices
            const activeDevices = deviceList.filter(device => !device.isRemoved);
            const removedDevices = deviceList.filter(device => device.isRemoved);

            // Calculate statistics (only for active devices)
            const onlineDevices = activeDevices.filter(device => {
              const realtimeDevice = devices.find(d => d.serial === device.serial);
              // Use device's own isOnline status first, then fallback to realtime check
              return device.isOnline || (realtimeDevice?.connectedAt ? true : false);
            });

            const watchingDevices = activeDevices.filter(device =>
              isOnline && activeSubscriptions.some(sub => sub.deviceSerial === device.serial)
            );
            
            return (
              <div className="space-y-6">
                {/* Device Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600 font-medium">Active Devices</p>
                    <p className="text-2xl font-bold text-blue-700">{activeDevices.length}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600 font-medium">Online Now</p>
                    <p className="text-2xl font-bold text-green-700">{onlineDevices.length}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-purple-600 font-medium">Watching</p>
                    <p className="text-2xl font-bold text-purple-700">{watchingDevices.length}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600 font-medium">Offline</p>
                    <p className="text-2xl font-bold text-gray-700">{activeDevices.length - onlineDevices.length}</p>
                  </div>
                  {removedDevices.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-600 font-medium">Removed</p>
                      <p className="text-2xl font-bold text-red-700">{removedDevices.length}</p>
                    </div>
                  )}
                </div>
                
                {/* Device List */}
                {deviceList.length > 0 ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">All User Devices</h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowAddDeviceModal(true)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          Add Device
                        </button>
                        <span className="text-xs text-gray-500">Status:</span>
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full" title="Online"></span>
                          <span className="text-xs text-gray-400">Online</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" title="Watching"></span>
                          <span className="text-xs text-gray-400">Watching</span>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <span className="w-2 h-2 bg-gray-400 rounded-full" title="Offline"></span>
                          <span className="text-xs text-gray-400">Offline</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {deviceList.map((device) => {
                        // Get real-time device status
                        const realtimeDevice = devices.find(d => d.serial === device.serial);
                        // Use device's own isOnline status first (from backend), then fallback to realtime check
                        const isDeviceOnline = device.isOnline || (realtimeDevice?.connectedAt ? true : false);
                        
                        // Check if user is currently watching this device
                        const isWatching = isOnline && activeSubscriptions.some(sub => sub.deviceSerial === device.serial);
                        const deviceSubscriptions = activeSubscriptions.filter(sub => sub.deviceSerial === device.serial);
                        
                        // Determine card style based on status
                        let cardBorderClass = 'border-gray-200';
                        let statusBgClass = 'bg-gray-50';
                        let cardOpacity = '';

                        if (device.isRemoved) {
                          // Device has been removed
                          cardBorderClass = 'border-red-200';
                          statusBgClass = 'bg-red-50';
                          cardOpacity = 'opacity-75';
                        } else if (isWatching && isDeviceOnline) {
                          // User is watching AND device is online
                          cardBorderClass = 'border-purple-300';
                          statusBgClass = 'bg-purple-50';
                        } else if (isWatching && !isDeviceOnline) {
                          // User is watching but device is offline
                          cardBorderClass = 'border-orange-300';
                          statusBgClass = 'bg-orange-50';
                        } else if (isDeviceOnline) {
                          // Device is online but not being watched
                          cardBorderClass = 'border-green-300';
                          statusBgClass = 'bg-green-50';
                        }
                        
                        return (
                          <div
                            key={`device-${device.serial}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Don't navigate for removed devices
                              if (!device.isRemoved) {
                                navigate(`/dashboard/devices/${device.serial}`);
                              }
                            }}
                            className={`relative bg-white border ${cardBorderClass} rounded-lg p-4 hover:shadow-md transition-all ${device.isRemoved ? 'cursor-not-allowed' : 'cursor-pointer'} ${cardOpacity}`}
                          >
                            {/* Status Badge and Delete Button */}
                            <div className="absolute -top-2 -right-2 flex items-center gap-1">
                              {device.isDefault && !device.isRemoved && (
                                <div className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow">
                                  DEFAULT
                                </div>
                              )}
                              {device.isRemoved ? (
                                <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow flex items-center gap-1">
                                  <XCircleIcon className="w-3 h-3" />
                                  REMOVED
                                </div>
                              ) : isWatching && isDeviceOnline ? (
                                <div className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow">
                                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                                  WATCHING
                                </div>
                              ) : isWatching && !isDeviceOnline ? (
                                <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow">
                                  WATCHING
                                </div>
                              ) : isDeviceOnline ? (
                                <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow">
                                  ONLINE
                                </div>
                              ) : (
                                <div className="bg-gray-400 text-white px-2 py-1 rounded-full text-xs font-semibold shadow">
                                  OFFLINE
                                </div>
                              )}
                              {/* Delete Button - only for active devices */}
                              {!device.isRemoved && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeviceToDelete(device);
                                    setShowDeleteDeviceModal(true);
                                  }}
                                  className="p-1.5 bg-red-100 hover:bg-red-200 text-red-600 rounded-full transition-colors shadow"
                                  title="Remove device access"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            
                            {/* Remove PRIMARY badge - it was confusing and incorrect */}
                            
                            {/* Device Info */}
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`relative p-2 rounded-lg ${statusBgClass}`}>
                                    <DeviceIcon model={device.model} className={`w-6 h-6 ${
                                      isWatching && isDeviceOnline ? 'text-purple-600' :
                                      isWatching && !isDeviceOnline ? 'text-orange-600' :
                                      isDeviceOnline ? 'text-green-600' : 
                                      'text-gray-400'
                                    }`} />
                                    {/* Online status dot on icon */}
                                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                                      isDeviceOnline ? 'bg-green-500' : 'bg-gray-400'
                                    }`}>
                                      {isDeviceOnline && (
                                        <span className="absolute inset-0 rounded-full bg-green-400 animate-ping"></span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {device.nickname ? (
                                        <div>
                                          <p className="text-sm font-bold text-gray-900">{device.nickname}</p>
                                          <p className="font-mono text-xs text-gray-500">{device.serial}</p>
                                        </div>
                                      ) : (
                                        <p className="font-mono text-sm font-bold text-gray-900">{device.serial}</p>
                                      )}
                                      {/* Inline status indicator */}
                                      <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                                        isDeviceOnline ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          isDeviceOnline ? 'bg-green-500' : 'bg-gray-400'
                                        }`}></span>
                                        {isDeviceOnline ? 'Online' : 'Offline'}
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-500">{modelLabel(device.model) || 'Dryer Master'}</p>
                                  </div>
                                </div>
                                <ChevronRightIcon className="w-4 h-4 mt-1 text-gray-400" />
                              </div>
                              
                              {/* Connection Info */}
                              <div className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <SignalIcon className={`w-3.5 h-3.5 ${
                                    isWatching && isDeviceOnline ? 'text-purple-500' :
                                    isWatching && !isDeviceOnline ? 'text-orange-500' :
                                    isDeviceOnline ? 'text-green-500' : 'text-gray-400'
                                  }`} />
                                  <span className={
                                    isWatching && isDeviceOnline ? 'text-purple-600 font-medium' :
                                    isWatching && !isDeviceOnline ? 'text-orange-600 font-medium' :
                                    isDeviceOnline ? 'text-green-600 font-medium' : 'text-gray-500'
                                  }>
                                    {isWatching && isDeviceOnline ? 'Live Monitoring' :
                                     isWatching && !isDeviceOnline ? 'Waiting for Device' :
                                     isDeviceOnline ? 'Connected' : 'Disconnected'}
                                  </span>
                                </div>
                                {(isDeviceOnline && realtimeDevice?.connectedAt) && (
                                  <div className="text-right">
                                    <span className="text-gray-500 text-[10px]">
                                      {(() => {
                                        const connectedTime = new Date(realtimeDevice.connectedAt);
                                        const now = new Date();
                                        const diffMs = now - connectedTime;
                                        const diffMinutes = Math.floor(diffMs / (1000 * 60));
                                        const diffHours = Math.floor(diffMinutes / 60);
                                        const diffDays = Math.floor(diffHours / 24);

                                        let duration = '';
                                        if (diffMinutes < 1) {
                                          duration = 'just now';
                                        } else if (diffMinutes < 60) {
                                          duration = `${diffMinutes} min`;
                                        } else if (diffHours < 24) {
                                          const remainingMins = diffMinutes % 60;
                                          if (remainingMins > 0 && diffHours < 3) {
                                            duration = `${diffHours}h ${remainingMins}m`;
                                          } else {
                                            duration = `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
                                          }
                                        } else {
                                          duration = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
                                        }

                                        if (isWatching && isDeviceOnline) {
                                          return `Monitoring for ${duration}`;
                                        } else {
                                          return `Connected ${duration}`;
                                        }
                                      })()}
                                    </span>
                                    <span className="text-gray-400 text-[9px] block">
                                      Since {new Date(realtimeDevice.connectedAt).toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Device Timeline Info */}
                              <div className="space-y-1.5 pt-2 border-t border-gray-100">
                                {device.createdAt && (
                                  <div className="text-xs flex items-center justify-between">
                                    <span className="text-gray-500">Registered:</span>
                                    <div className="text-right">
                                      <span className="text-gray-700 font-medium">
                                        {(() => {
                                          const date = new Date(device.createdAt);
                                          const now = new Date();
                                          const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

                                          if (diffDays === 0) return 'Today';
                                          if (diffDays === 1) return 'Yesterday';
                                          if (diffDays < 7) return `${diffDays} days ago`;
                                          if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
                                          if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
                                          return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
                                        })()}
                                      </span>
                                      <span className="text-gray-400 text-[10px] block">
                                        {new Date(device.createdAt).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {device.lastSeen && (
                                  <div className="text-xs flex items-center justify-between">
                                    <span className="text-gray-500">Last Seen:</span>
                                    <div className="text-right">
                                      <span className={`font-medium ${
                                        device.isOnline ? 'text-green-600' : 'text-gray-700'
                                      }`}>
                                        {device.isOnline ? 'Online Now' : (() => {
                                          const date = new Date(device.lastSeen);
                                          const now = new Date();
                                          const diffMinutes = Math.floor((now - date) / (1000 * 60));
                                          const diffHours = Math.floor(diffMinutes / 60);
                                          const diffDays = Math.floor(diffHours / 24);

                                          if (diffMinutes < 1) return 'Just now';
                                          if (diffMinutes < 60) return `${diffMinutes} min ago`;
                                          if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
                                          if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
                                          return new Date(device.lastSeen).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric'
                                          });
                                        })()}
                                      </span>
                                      {!device.isOnline && (
                                        <span className="text-gray-400 text-[10px] block">
                                          {new Date(device.lastSeen).toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Removed Device Info */}
                              {device.isRemoved && device.removedAt && (
                                <div className="pt-2 border-t border-red-100">
                                  <div className="bg-red-50 rounded p-2">
                                    <div className="flex items-center gap-2 text-xs text-red-700">
                                      <ClockIcon className="w-3.5 h-3.5" />
                                      <span className="font-medium">Access Removed</span>
                                    </div>
                                    <p className="text-xs text-red-600 mt-1">
                                      {new Date(device.removedAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                    {device.removedBy && (
                                      <p className="text-xs text-red-600 mt-1">
                                        <span className="font-medium">Removed by:</span>{' '}
                                        {typeof device.removedBy === 'object' ? (
                                          <>
                                            {device.removedBy.firstName} {device.removedBy.lastName}
                                            {device.removedBy.role && (
                                              <span className="text-red-500"> ({device.removedBy.role})</span>
                                            )}
                                          </>
                                        ) : (
                                          'Employee'
                                        )}
                                      </p>
                                    )}
                                    {device.removalReason && (
                                      <p className="text-xs text-red-500 mt-1 italic">
                                        Reason: {device.removalReason}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Set as Default Button */}
                              {!device.isDefault && !device.isRemoved && (
                                <div className="pt-2 border-t border-gray-100">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetDefaultDevice(device.serial);
                                    }}
                                    className="w-full py-1.5 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded transition-colors"
                                  >
                                    Set as Default Device
                                  </button>
                                </div>
                              )}
                              
                              {/* Monitoring Topics */}
                              {isWatching && deviceSubscriptions.length > 0 && (
                                <div className="pt-2 border-t border-gray-100">
                                  <p className="text-xs text-gray-600 mb-1.5 font-medium">Monitoring Topics:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {deviceSubscriptions.map((sub, idx) => (
                                      <span key={`${device.serial}-sub-${idx}`} className={`text-xs px-2 py-0.5 rounded font-medium ${
                                        isDeviceOnline ? 
                                          'bg-purple-100 text-purple-700' : 
                                          'bg-orange-100 text-orange-700'
                                      }`}>
                                        {sub.topic}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <DeviceIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No devices associated with this user</p>
                    <p className="text-sm text-gray-400 mt-1 mb-4">Add devices to give this user access</p>
                    <button
                      onClick={() => setShowAddDeviceModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add First Device
                    </button>
                  </div>
                )}
                
                {/* Live User Connection Card - Collapsible */}
                {isOnline && (
                  <div className="mt-6">
                    <LiveConnectionCard 
                      socketDebugInfo={socketDebugInfo}
                      activeSubscriptions={activeSubscriptions}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </CollapsibleSection>

        {/* Personal Information - Closed by default */}
        <CollapsibleSection
          title="Personal Information"
          icon={InformationCircleIcon}
          defaultOpen={false}
        >
          <div className="space-y-6">
            {/* Edit Button */}
            <div className="flex justify-end">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit Information
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            {/* Personal Details */}
            {isEditing ? (
              <div className="space-y-6 bg-blue-50 rounded-lg p-6 border border-blue-200">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">Edit Personal Information</h3>
                  <p className="text-xs text-blue-700">Fields marked with * are required</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EditableInfoItem
                    icon={UserIcon}
                    label="First Name"
                    value={editedUser.firstName}
                    field="firstName"
                    onChange={handleFieldChange}
                    placeholder="Enter first name"
                    required={true}
                  />
                  <EditableInfoItem
                    icon={UserIcon}
                    label="Last Name"
                    value={editedUser.lastName}
                    field="lastName"
                    onChange={handleFieldChange}
                    placeholder="Enter last name"
                    required={true}
                  />
                  <EditableInfoItem
                    icon={BuildingOfficeIcon}
                    label="Farm Name"
                    value={editedUser.farmName}
                    field="farmName"
                    onChange={handleFieldChange}
                    placeholder="Enter farm name"
                    required={true}
                  />
                  <EditableInfoItem
                    icon={EnvelopeIcon}
                    label="Email Address"
                    value={editedUser.email}
                    field="email"
                    onChange={handleFieldChange}
                    type="email"
                    placeholder="email@example.com"
                    required={true}
                  />
                  <EditableInfoItem
                    icon={PhoneIcon}
                    label="Phone Number"
                    value={editedUser.cellPhone}
                    field="cellPhone"
                    onChange={handleFieldChange}
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                  />
                  <EditableInfoItem
                    icon={CalendarIcon}
                    label="Date of Birth"
                    value={editedUser.dob ? new Date(editedUser.dob).toISOString().split('T')[0] : ''}
                    field="dob"
                    onChange={handleFieldChange}
                    type="date"
                  />
                  
                  {/* Gender Selection */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                      Gender
                    </label>
                    <select
                      value={editedUser.gender || ''}
                      onChange={(e) => handleFieldChange('gender', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                 transition-colors duration-200 hover:border-gray-400"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <InfoItem
                  icon={UserIcon}
                  label="Full Name"
                  value={`${user.firstName} ${user.lastName}`}
                />
                <InfoItem
                  icon={BuildingOfficeIcon}
                  label="Farm Name"
                  value={user.farmName}
                />
                <InfoItem
                  icon={EnvelopeIcon}
                  label="Email"
                  value={user.email}
                />
                {user.cellPhone && (
                  <InfoItem
                    icon={PhoneIcon}
                    label="Phone"
                    value={user.cellPhone}
                  />
                )}
                {user.dob && (
                  <InfoItem
                    icon={CalendarIcon}
                    label="Date of Birth"
                    value={new Date(user.dob).toLocaleDateString()}
                  />
                )}
                <InfoItem
                  icon={UserIcon}
                  label="Role"
                  value={user.role}
                />
              </div>
            )}

            {/* Address */}
            {(user.address || isEditing) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                {isEditing ? (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4" />
                      Address Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Street Address
                        </label>
                        <input
                          type="text"
                          value={editedUser.address?.street || ''}
                          onChange={(e) => handleAddressChange('street', e.target.value)}
                          placeholder="123 Main Street"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   placeholder-gray-400 transition-colors duration-200
                                   hover:border-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={editedUser.address?.city || ''}
                          onChange={(e) => handleAddressChange('city', e.target.value)}
                          placeholder="New York"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   placeholder-gray-400 transition-colors duration-200
                                   hover:border-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State / Province
                        </label>
                        <input
                          type="text"
                          value={editedUser.address?.state || ''}
                          onChange={(e) => handleAddressChange('state', e.target.value)}
                          placeholder="NY"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   placeholder-gray-400 transition-colors duration-200
                                   hover:border-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ZIP / Postal Code
                        </label>
                        <input
                          type="text"
                          value={editedUser.address?.zipCode || ''}
                          onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                          placeholder="10001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   placeholder-gray-400 transition-colors duration-200
                                   hover:border-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={editedUser.address?.country || ''}
                          onChange={(e) => handleAddressChange('country', e.target.value)}
                          placeholder="United States"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
                                   placeholder-gray-400 transition-colors duration-200
                                   hover:border-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-gray-500" />
                      Address Information
                    </h4>
                    <div className="space-y-2">
                      {/* Street Address */}
                      {user.address.street && (
                        <div className="text-sm text-gray-900">
                          {user.address.street}
                        </div>
                      )}
                      {/* City, State, ZIP */}
                      {(user.address.city || user.address.state || user.address.zipCode) && (
                        <div className="text-sm text-gray-900">
                          {[user.address.city, user.address.state, user.address.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                      {/* Country */}
                      {user.address.country && (
                        <div className="text-sm text-gray-900 font-medium">
                          {user.address.country}
                        </div>
                      )}
                      {/* If we have formatted address and it's different from what we built */}
                      {user.address.formattedAddress && 
                       !user.address.street && 
                       !user.address.city && 
                       !user.address.state && 
                       !user.address.zipCode && 
                       !user.address.country && (
                        <div className="text-sm text-gray-900">
                          {user.address.formattedAddress}
                        </div>
                      )}
                      {/* Show "No address provided" if no address fields are filled */}
                      {!user.address.street && 
                       !user.address.city && 
                       !user.address.state && 
                       !user.address.zipCode && 
                       !user.address.country && 
                       !user.address.formattedAddress && (
                        <div className="text-sm text-gray-500 italic">
                          No address information provided
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Payment & Annual Access - closed by default */}
        <CollapsibleSection
          title="Payment & Annual Access"
          icon={CreditCardIcon}
          defaultOpen={false}
        >
          <PaymentInformation
            user={user}
            onUpdate={(updatedUser) => {
              setUser(updatedUser);
            }}
          />
        </CollapsibleSection>

        {/* Notification Preferences - Under Device Information */}
        <CollapsibleSection
          title="Notification Preferences"
          icon={BellIcon}
          defaultOpen={false}
        >
          <NotificationPreferencesEditor 
            userId={user._id}
            preferences={user.notificationPreferences || {}}
            onUpdate={(updatedPreferences) => {
              setUser(prev => ({
                ...prev,
                notificationPreferences: updatedPreferences
              }));
            }}
          />
        </CollapsibleSection>

        {/* User Activity & Session History - developer only, at the bottom and collapsed by default */}
        {isDeveloper && (
          <CollapsibleSection
            title="Activity & Session History"
            icon={ChartBarIcon}
            defaultOpen={false}
          >
            <UserSessionActivity userId={id} devices={devices} />
          </CollapsibleSection>
        )}
      </div>

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        user={user}
        onConfirm={handleStatusChange}
        isLoading={statusChangeLoading}
      />

      {/* Add Device Modal */}
      <AnimatePresence>
        {showAddDeviceModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddDeviceModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Add Device to User</h3>
                  <button
                    onClick={() => setShowAddDeviceModal(false)}
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Search Bar */}
                <div className="mt-4 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by serial, model, or location..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Device List */}
              <div className="px-6 py-4 max-h-[400px] overflow-y-auto">
                {addDeviceLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading devices...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="w-10 h-10 text-red-500 mx-auto mb-2" />
                    <p className="text-red-600">{error}</p>
                  </div>
                ) : filteredDevices.length === 0 ? (
                  <div className="text-center py-8">
                    <DeviceIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No devices found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredDevices.map((device) => {
                      const isSelected = selectedDevice?.serial === device.serial;
                      const canAdd = !device.currentUserHasDevice;

                      return (
                        <div
                          key={device.serial}
                          onClick={() => canAdd && setSelectedDevice(device)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : device.currentUserHasDevice
                              ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <DeviceIcon model={device.model} className={`w-5 h-5 mt-0.5 ${
                                device.currentUserHasDevice ? 'text-gray-400' : 'text-gray-600'
                              }`} />
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{device.serial}</p>
                                  <span className="text-xs text-gray-500">
                                    {modelLabel(device.model) || 'Dryer Master'}
                                  </span>
                                </div>

                                {/* Device Status Indicators */}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {device.currentUserHasDevice && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                                      <CheckCircleIcon className="w-3 h-3" />
                                      Already Added
                                    </span>
                                  )}

                                  {device.passwordUsed && (
                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1">
                                      <LockClosedIcon className="w-3 h-3" />
                                      Password Used
                                    </span>
                                  )}

                                  {device.assignedUsers && device.assignedUsers.length > 0 && !device.currentUserHasDevice && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                                      <UserGroupIcon className="w-3 h-3" />
                                      {device.assignedUsers.length} User{device.assignedUsers.length > 1 ? 's' : ''}
                                    </span>
                                  )}

                                  {device.isAvailable && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                      Available
                                    </span>
                                  )}

                                  {device.isOnline && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                      Online
                                    </span>
                                  )}
                                </div>

                                {/* Location Info */}
                                {device.location?.city && (
                                  <p className="text-xs text-gray-500">
                                    {[device.location.city, device.location.region, device.location.country]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </p>
                                )}

                                {/* Show assigned users */}
                                {device.assignedUsers && device.assignedUsers.length > 0 && !device.currentUserHasDevice && (
                                  <div className="mt-1">
                                    <p className="text-xs text-gray-600">Assigned to:</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {device.assignedUsers.slice(0, 3).map((owner) => (
                                        <span key={owner._id} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                          {owner.email}
                                        </span>
                                      ))}
                                      {device.assignedUsers.length > 3 && (
                                        <span className="text-xs text-gray-500">
                                          +{device.assignedUsers.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {isSelected && (
                              <CheckCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Nickname Input (when device is selected) */}
              {selectedDevice && !selectedDevice.currentUserHasDevice && (
                <div className="px-6 pb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Nickname (Optional)
                  </label>
                  <input
                    type="text"
                    value={deviceNickname}
                    onChange={(e) => setDeviceNickname(e.target.value)}
                    placeholder="e.g., Main Dryer, North Facility"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowAddDeviceModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddDevice}
                  disabled={!selectedDevice || selectedDevice.currentUserHasDevice || addDeviceLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addDeviceLoading ? 'Adding...' : 'Add Device'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Device Modal */}
      <AnimatePresence>
        {showDeleteDeviceModal && deviceToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowDeleteDeviceModal(false);
              setDeleteReason('');
              setCustomDeleteReason('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-full bg-red-100">
                    <TrashIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Remove Device Access
                  </h3>
                </div>

                <div className="space-y-3 text-sm text-gray-600">
                  <p>
                    You are about to remove access to this device for:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                    <p className="text-gray-600">{user?.email}</p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-orange-800 font-medium mb-1">Device Details:</p>
                    <p className="text-orange-700">{deviceToDelete.nickname || deviceToDelete.serial}</p>
                    <p className="text-xs text-orange-600">{modelLabel(deviceToDelete.model) || 'Dryer Master'} • {deviceToDelete.serial}</p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 font-medium mb-1">This will:</p>
                    <ul className="text-red-700 space-y-1 list-disc list-inside text-xs">
                      <li>Revoke the user's access to this device</li>
                      <li>Stop all data monitoring for this device</li>
                      <li>Prevent the user from viewing device data</li>
                      <li>Keep a record of the removal for audit purposes</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-800 text-xs">
                      <strong>Note:</strong> The device will remain in the user's history as "removed" and can be re-added later if needed.
                    </p>
                  </div>

                  {/* Reason Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Reason for Removal <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={deleteReason}
                      onChange={(e) => {
                        setDeleteReason(e.target.value);
                        if (e.target.value !== 'custom') {
                          setCustomDeleteReason('');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Select a reason...</option>
                      <option value="Device defective">Device defective/faulty</option>
                      <option value="Device replaced">Device replaced with new unit</option>
                      <option value="Device resold">Device resold to another customer</option>
                      <option value="User no longer needs access">User no longer needs access</option>
                      <option value="Employee terminated">Employee terminated/left company</option>
                      <option value="Contract expired">Contract/subscription expired</option>
                      <option value="Security concern">Security or compliance concern</option>
                      <option value="Testing completed">Testing/trial period completed</option>
                      <option value="custom">Other (specify below)</option>
                    </select>

                    {/* Custom Reason Input */}
                    {deleteReason === 'custom' && (
                      <div className="mt-2">
                        <textarea
                          value={customDeleteReason}
                          onChange={(e) => setCustomDeleteReason(e.target.value)}
                          placeholder="Please enter the specific reason for removing this device..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteDeviceModal(false);
                    setDeleteReason('');
                    setCustomDeleteReason('');
                  }}
                  disabled={deleteDeviceLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDevice}
                  disabled={deleteDeviceLoading || (!deleteReason || (deleteReason === 'custom' && !customDeleteReason))}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteDeviceLoading ? 'Removing...' : 'Remove Access'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UserDetail;